import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import { continueInRovoChat } from '@atlaskit/editor-common/keymaps';
import { fg } from '@atlaskit/platform-feature-flags';
import { type EditorAgent } from '@atlassian/generative-ai-modal/utils/agents';

import { type EditorPluginAIProvider } from '../../../../types/types';
import { ChatIcon } from '../../../icons/prompt/chat';
import type {
	ActionAppearance,
	EditorPluginAIConfigItemAgentAction,
	EditorPluginAIConfigItemGenericChatAction,
} from '../config-items';

type CreateContinueInChatProps = {
	actionOverride?: ({ channelId }: { channelId: string }) => void;
	actionSideEffect?: () => void;
	appearance: ActionAppearance;
	product: EditorPluginAIProvider['product'];
};

export const createContinueInChat = ({
	appearance,
	product,
	actionSideEffect,
}: CreateContinueInChatProps): EditorPluginAIConfigItemGenericChatAction => ({
	type: 'generic-chat',
	title: fg('aifc_create_enabled')
		? aiConfigItemMessages.configActionContinueChatLabel
		: aiConfigItemMessages.configActionContinueInChatLabel,
	appearance,
	shortcut: continueInRovoChat,
	run: ({ name, dialogues, publish }) => {
		if (!publish || !dialogues.length) {
			return;
		}

		publish({ type: 'chat-new', product, data: { dialogues, name }, source: 'editor' });

		actionSideEffect?.();
	},
	preserveEditorSelectionOnComplete: true,
	icon: ChatIcon,
});

type CreateContinueInChatForAgentProps = CreateContinueInChatProps & { agent: EditorAgent };

export const createContinueInChatForAgent = ({
	appearance,
	agent,
	product,
	actionSideEffect,
	actionOverride,
}: CreateContinueInChatForAgentProps): EditorPluginAIConfigItemAgentAction => ({
	type: 'agent',
	title: fg('aifc_create_enabled')
		? aiConfigItemMessages.configActionContinueChatLabel
		: aiConfigItemMessages.configActionContinueInChatLabel,
	shortcut: continueInRovoChat,
	appearance,
	run: ({ channelId, publish }) => {
		if (!channelId || !publish) {
			return;
		}

		if (typeof actionOverride === 'function') {
			actionOverride({ channelId });
			return;
		}

		publish({
			type: 'chat-open',
			product,
			data: { channelId, agentId: agent.namedId },
			source: 'editor',
		});

		actionSideEffect?.();
	},
	preserveEditorSelectionOnComplete: true,
	icon: ChatIcon,
});
